import { Stack, SxProps, Theme } from "@mui/material";

import AppIconButton from "@/common/components/Button/AppIconButton";
import ContactInput, { ContactInputProps } from "@/common/components/Contact/ContactInput";
import AppIcon from "@/common/components/Icons/AppIcon";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { GeneralContactInputDto } from "@/core/api/generated/v1";

interface Props extends Omit<ContactInputProps, "value" | "onChange"> {
  value: Nil<GeneralContactInputDto>;
  withClearButton?: boolean;
  withMePrefillButton?: boolean;
  sx?: SxProps<Theme>;
  onChange: (newValue: Nil<GeneralContactInputDto>) => void;
}

export function GeneralContactInput({
  value,
  displayProps,
  withClearButton = false,
  withMePrefillButton = false,
  sx,
  onChange,
  ...otherProps
}: Props) {
  const profile = useUserProfile();

  return (
    <ContactInput
      sx={sx}
      value={{
        firstName: value?.personName?.firstName || undefined,
        lastName: value?.personName?.lastName || undefined,
        email: value?.email || undefined,
        phoneNumber: value?.phoneNumber || undefined,
      }}
      displayProps={{
        firstName: true,
        lastName: true,
        email: true,
        phoneNumber: true,
        nationalIdentityNumber: false,
        isPrimary: false,
        ...displayProps,
      }}
      action={
        (withClearButton || withMePrefillButton) && (
          <Stack>
            {withClearButton && (
              <AppIconButton
                tooltipProps={{ title: "Clear", enterDelay: 300, enterNextDelay: 300 }}
                onClick={() => {
                  onChange(null);
                }}
              >
                <AppIcon of='clear' />
              </AppIconButton>
            )}

            {withMePrefillButton && profile && (
              <AppIconButton
                tooltipProps={{
                  title: "Use my contact data",
                  enterDelay: 300,
                  enterNextDelay: 300,
                }}
                onClick={() => {
                  onChange({
                    personName: profile.personName
                      ? {
                          firstName: profile.personName?.firstName || undefined,
                          lastName: profile.personName?.lastName || undefined,
                        }
                      : undefined,
                    email: profile.email || undefined,
                    phoneNumber: profile.phoneNumber || undefined,
                  });
                }}
              >
                <AppIcon of='profile' />
              </AppIconButton>
            )}
          </Stack>
        )
      }
      onChange={(newValue) => {
        onChange({
          personName:
            newValue?.firstName || newValue?.lastName
              ? {
                  firstName: newValue?.firstName,
                  lastName: newValue?.lastName,
                }
              : undefined,
          email: newValue.email,
          phoneNumber: newValue.phoneNumber,
        });
      }}
      {...otherProps}
    />
  );
}
